import { 
    InsuranceClaim, 
    InsuranceClaimFormData,
    InsuranceClaimApiPayload,
    CreateClaimResponse, 
    UpdateClaimResponse, 
    GetClaimsResponse, 
    DeleteClaimResponse,
    ApiError,
    ApiResult
} from '../types';

/**
 * API Service for Insurance Claims
 * Handles all API interactions with the backend insurance claims endpoints
 */
export class InsuranceClaimsApiService {
    private baseUrl = import.meta.env.VITE_APP_API_URL + "/billing";

    /**
     * Create a new insurance claim for a specific bill
     * @param uuidInvestigation - Investigation UUID
     * @param uuidBill - Bill UUID
     * @param claimData - Form data for the claim
     * @param idempotencyKey - Optional idempotency key
     * @returns Promise<ApiResult<InsuranceClaim>>
     */
    async createClaim(
        uuidInvestigation: string, 
        uuidBill: string, 
        claimData: InsuranceClaimApiPayload,
        idempotencyKey?: string
    ): Promise<ApiResult<InsuranceClaim>> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/bill/${uuidBill}/claims`;
        
        try {
            const headers: Record<string, string> = { 
                'Content-Type': 'application/json',
                'Authorization': localStorage.getItem("jwt") || ''
            };
            if (idempotencyKey) {
                headers['Idempotency-Key'] = idempotencyKey;
            }
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(claimData)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result: CreateClaimResponse = await response.json();
            
            // Handle different response structures - some APIs return the data directly
            if (!result.success && result.success !== undefined) {
                throw new ApiError({
                    message: result.message || 'Failed to create claim',
                    statusCode: 400,
                    details: result
                });
            }
            
            // If success is undefined, assume the response is valid if we have insuranceClaim
            if (!result.insuranceClaim) {
                // Try alternative response structures
                const insuranceClaim = (result as any).data || (result as any);
                if (!insuranceClaim) {
                    console.error('Invalid response structure - no claim data found:', result);
                    throw new ApiError({
                        message: 'Invalid response structure from API - no claim data found',
                        statusCode: 400,
                        details: result
                    });
                }
                // Validate it has at least an id or uuid
                if (!insuranceClaim.id && !insuranceClaim.uuid) {
                    console.error('Invalid response structure - claim missing id/uuid:', insuranceClaim);
                    throw new ApiError({
                        message: 'Invalid response structure from API - claim missing required fields',
                        statusCode: 400,
                        details: insuranceClaim
                    });
                }
                return { data: insuranceClaim, status: response.status };
            }
            
            return { data: result.insuranceClaim, status: response.status };
            
        } catch (error) {
            console.error('Failed to create claim:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while creating claim',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Update an existing insurance claim
     * @param uuidInvestigation - Investigation UUID
     * @param uuidClaim - Claim UUID to update
     * @param updateData - Updated form data
     * @param idempotencyKey - Optional idempotency key
     * @returns Promise<ApiResult<InsuranceClaim>>
     */
    async updateClaim(
        uuidInvestigation: string,
        uuidClaim: string, 
        updateData: Partial<InsuranceClaimApiPayload>,
        idempotencyKey?: string
    ): Promise<ApiResult<InsuranceClaim>> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/claims/${uuidClaim}`;
        
        try {
            const headers: Record<string, string> = { 
                'Content-Type': 'application/json',
                'Authorization': localStorage.getItem("jwt") || ''
            };
            if (idempotencyKey) {
                headers['Idempotency-Key'] = idempotencyKey;
            }
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers,
                body: JSON.stringify(updateData)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result: UpdateClaimResponse = await response.json();
            
            // Handle different response structures - some APIs return the data directly
            if (!result.success && result.success !== undefined) {
                throw new ApiError({
                    message: result.message || 'Failed to update claim',
                    statusCode: 400,
                    details: result
                });
            }
            
            // If success is undefined, assume the response is valid if we have insuranceClaim
            if (!result.insuranceClaim) {
                // Try alternative response structures
                const insuranceClaim = (result as any).data || (result as any);
                if (!insuranceClaim || !insuranceClaim.uuid) {
                    throw new ApiError({
                        message: 'Invalid response structure from API',
                        statusCode: 400,
                        details: result
                    });
                }
                return { data: insuranceClaim, status: response.status };
            }
            
            return { data: result.insuranceClaim, status: response.status };
            
        } catch (error) {
            console.error('Failed to update claim:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while updating claim',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Get all claims for a specific bill
     * @param uuidInvestigation - Investigation UUID
     * @param uuidBill - Bill UUID
     * @returns Promise<InsuranceClaim[]>
     */
    async getClaimsForBill(
        uuidInvestigation: string,
        uuidBill: string
    ): Promise<InsuranceClaim[]> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/bill/${uuidBill}/claims`;
        
        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem("jwt") || ''
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result: GetClaimsResponse = await response.json();
            
            // Handle different response structures - some APIs return the data directly
            if (!result.success && result.success !== undefined) {
                throw new ApiError({
                    message: result.message || 'Failed to fetch claims',
                    statusCode: 400,
                    details: result
                });
            }
            
            // If success is undefined or true, check for insuranceClaims array
            let claims = result.insuranceClaims;
            if (!claims) {
                // Try alternative response structures
                claims = (result as any).data || (result as any);
                if (!Array.isArray(claims)) {
                    // If it's not an array, try to extract from different possible structures
                    claims = (result as any).claims || [];
                }
            }
            
            return Array.isArray(claims) ? claims : [];
            
        } catch (error) {
            console.error('Failed to fetch claims:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while fetching claims',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Get all claims for a specific investigation
     * @param uuidInvestigation - Investigation UUID
     * @returns Promise<InsuranceClaim[]>
     */
    async getClaimsForInvestigation(uuidInvestigation: string): Promise<InsuranceClaim[]> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/claims`;
        
        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem("jwt") || ''
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result: GetClaimsResponse = await response.json();
            
            // Handle different response structures - some APIs return the data directly
            if (!result.success && result.success !== undefined) {
                throw new ApiError({
                    message: result.message || 'Failed to fetch claims',
                    statusCode: 400,
                    details: result
                });
            }
            
            // If success is undefined or true, check for insuranceClaims array
            let claims = result.insuranceClaims;
            if (!claims) {
                // Try alternative response structures
                claims = (result as any).data || (result as any);
                if (!Array.isArray(claims)) {
                    // If it's not an array, try to extract from different possible structures
                    claims = (result as any).claims || [];
                }
            }
            
            return Array.isArray(claims) ? claims : [];
            
        } catch (error) {
            console.error('Failed to fetch claims:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while fetching claims',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Delete a claim (only DRAFT status allowed)
     * @param uuidInvestigation - Investigation UUID
     * @param uuidClaim - Claim UUID to delete
     * @returns Promise<void>
     */
    async deleteClaim(
        uuidInvestigation: string,
        uuidClaim: string
    ): Promise<void> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/claims/${uuidClaim}`;
        
        try {
            const response = await fetch(endpoint, {
                method: 'DELETE',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem("jwt") || ''
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result: DeleteClaimResponse = await response.json();
            
            if (!result.success) {
                throw new ApiError({
                    message: result.message || 'Failed to delete claim',
                    statusCode: 400,
                    details: result
                });
            }
            
        } catch (error) {
            console.error('Failed to delete claim:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while deleting claim',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Fetch organizations for the investigation
     * @param uuidInvestigation - Investigation UUID
     * @returns Promise<Array<{id: number, name: string, uuid?: string}>>
     */
    async getOrganizations(
        uuidInvestigation: string
    ): Promise<Array<{id: number, name: string, uuid?: string}>> {
        const endpoint = `${this.baseUrl}/investigation/${uuidInvestigation}/organizations`;
        
        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem("jwt") || ''
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    statusCode: response.status,
                    details: errorData
                });
            }
            
            const result = await response.json();
            
            // Handle different possible response structures
            // Common patterns: result.organizations, result.data, or result directly as array
            const organizations = result.organizations || result.data || (Array.isArray(result) ? result : []);
            
            // Ensure we return an array with the expected structure
            return organizations.map((org: any) => ({
                id: org.id,
                name: org.name,
                uuid: org.uuid
            }));
            
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError({
                message: 'Network error occurred while fetching organizations',
                statusCode: 0,
                details: error
            });
        }
    }

    /**
     * Mock API call for development/testing
     * This can be used when the backend is not ready
     */
    async mockApiCall<T>(data: T, delay: number = 1000): Promise<T> {
        console.log('Mock API call with data:', data);
        
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(data);
            }, delay);
        });
    }
}

// Export a singleton instance
export const insuranceClaimsApi = new InsuranceClaimsApiService(); 